// Resource shapes shared by /v1 and /portal/v1 (§11A.4: same resource shapes as the public API).

export type ItemStatus =
  | "RECEIVED" | "FETCHED" | "FETCH_FAILED" | "VALIDATED" | "DUPLICATE" | "QUARANTINED" | "PURGED"
  | "NEEDS_INPUT" | "TRIAGED" | "NEEDS_REVIEW" | "ACCEPTED";

export interface Triage {
  decision: ItemStatus;
  reason_code: string | null;
  confidence: number;
  reasons: string[];
  layers_run?: string[];
  models?: Record<string, string>;
  policy_version?: string;
  decided_by?: string;
  overrides?: { by: string; at: string; decision: string; reason: string }[];
}

export interface Item {
  id: string;
  /** Only on a single item read: the copy in the firm's drive, or why there is none. */
  drive_filing?: { state: "filed" | "pending" | "problem" | "not_filed"; path: string | null; reason: string | null } | null;
  kind: "file" | "message" | "voice" | "data";
  status: ItemStatus;
  reason_code: string | null;
  client_org_id: string | null;
  source: string;
  filename: string | null;
  mime: string | null;
  size_bytes: number | null;
  sha256: string | null;
  doc_type: string | null;
  doc_type_confidence: number | null;
  period: string | null;
  fy: string | null;
  vault_path: string | null;
  duplicate_of: string | null;
  previous_version_id: string | null;
  parent_item_id: string | null;
  version: number;
  received_at: string;
  updated_at: string;
  sender: { phone?: string; name?: string | null; email?: string } | null;
  context: Record<string, unknown>;
  hints: { gstins?: string[]; doc_no?: string; doc_date?: string };
  triage: Triage | null;
  content_url: string | null;
  versions?: string[];
}

export interface Page<T> { data: T[]; next_cursor?: string | null }

export interface ClientOrg {
  id: string; external_ref: string; display_name: string; legal_name?: string | null; pan: string | null;
  gstins: string[]; phones?: string[]; status: string;
}

export interface Membership { tenant_id: string; name: string; role: "owner" | "admin" | "staff" | "viewer" }

export type Edition = "ca" | "accounting";

export interface Me {
  id: string; name: string | null; phone: string; email: string | null; tenant_id: string;
  role: Membership["role"]; client_access: "all" | "assigned"; memberships: Membership[];
  firm: { name: string; kind: "ca_firm" | "business" | "platform"; editions: Edition[] } | null;
  /** Owners confirm with an authenticator app once the firm turns on filing or posting. */
  owner_totp?: boolean;
  /** The shared AawazAI WhatsApp number as people dial it, and whether client groups can be created on it yet. */
  whatsapp?: { number: string | null; groups: boolean };
  /** The firm's short name, as used in its email addresses. */
  firm_slug?: string | null;
}

export interface Connection {
  id: string; connector_key: string; display_name: string; status: string; config: Record<string, unknown>;
  health: Record<string, unknown>; last_error: string | null; created_at: string;
  /** WhatsApp on the AawazAI number, which every firm shares (no credentials of the firm's own). */
  shared?: boolean;
}

export interface Command {
  id: string; type: string; status: string; error_code: string | null; result: Record<string, unknown> | null;
  created_at: string; completed_at: string | null; approval: Record<string, unknown> | null;
}

export const DOC_TYPES = [
  "sales_invoice", "purchase_invoice", "expense_bill", "credit_note", "debit_note", "receipt", "payment_proof_upi",
  "bank_statement", "eway_bill", "einvoice", "delivery_challan", "purchase_order", "quotation", "gst_notice",
  "gst_return_doc", "tds_certificate", "salary_payroll", "rent_agreement", "ledger_export", "tally_export",
  "other_financial", "unknown",
] as const;
